/**
 * Contract for `GET /api/alerts/analytics/` (MIN-227).
 *
 * Analytics answers "how much work arrived, how much got reviewed, and what
 * were the outcomes" for a period. It is NOT the queue, not ingest monitoring,
 * and explicitly not model-accuracy reporting — outcomes are workflow decisions
 * an operator made, and labelling them as accuracy would turn a coverage view
 * into a scoreboard for the model.
 */

export interface AlertAnalyticsOutcomeItem {
  code: string;
  count: number;
  share: number | null;
}

export interface AlertAnalyticsOldestPending {
  alert_id: number;
  waiting_since: string;
  waiting_since_basis: string;
  estimated: boolean;
}

/**
 * Per-reviewer INITIAL decisions — the first terminal decision in a review
 * cycle. Deliberately NOT reconciled against the team's FINAL `outcomes`: a
 * later correction moves the final outcome without changing who decided what
 * first, so forcing the two to agree would erase a real distinction.
 */
export interface AlertAnalyticsOutcomeCounts {
  confirmed_signal: number;
  false_alert: number;
  duplicate: number;
  insufficient_evidence: number;
  other: number;
  unclassified_legacy: number;
}

/**
 * Analytics-safe role. Internal roles (admin/owner) are never exposed through
 * analytics — the backend narrows them to these three.
 */
export type AlertAnalyticsReviewerRole = "operator" | "supervisor" | "reviewer";

export interface AlertAnalyticsReviewerSummary {
  principal_type: "user";
  /** Stable identity — the React key. Never key on the label. */
  principal_id: string;
  principal_label: string | null;
  role_code: AlertAnalyticsReviewerRole;
  reviews_completed: number;
  initial_outcome_counts: AlertAnalyticsOutcomeCounts;
}

export interface AlertAnalyticsReviewerTail {
  reviewer_count: number;
  reviews_completed: number;
  initial_outcome_counts: AlertAnalyticsOutcomeCounts;
}

export interface AlertAnalyticsUnattributed {
  reviews_completed: number;
  initial_outcome_counts: AlertAnalyticsOutcomeCounts;
}

/**
 * Attribution partitions `alerts_reviewed` into named reviewers, an anonymous
 * tail, and reviews nobody could be attributed to.
 */
export interface AlertAnalyticsReviewerBreakdown {
  alerts_reviewed: number;
  reviewers: AlertAnalyticsReviewerSummary[];
  other_reviewers: AlertAnalyticsReviewerTail | null;
  unattributed: AlertAnalyticsUnattributed | null;
  truncated: boolean;
}

export interface AlertAnalyticsTrendPoint {
  start_time: string;
  end_time: string;
  alerts_received: number;
  alerts_reviewed: number;
}

export interface AlertAnalyticsPeriodReport {
  queue_flow: {
    pending_at_start: number;
    alerts_received: number;
    alerts_reviewed: number;
    net_queue_change: number;
    pending_at_effective_end: number;
  };
  reviewer_breakdown: AlertAnalyticsReviewerBreakdown;
  outcomes: {
    alerts_reviewed: number;
    items: AlertAnalyticsOutcomeItem[];
    legacy_defaulted_disposition_count: number;
    unclassified_legacy_count: number;
  };
  trend: AlertAnalyticsTrendPoint[];
  coverage?: {
    status: "complete" | "partial";
    excluded_legacy_reviews: number;
    effective_start_time: string;
  } | null;
}

export interface AlertAnalyticsShift {
  shift_key: string;
  label: string;
  status: "complete" | "in_progress" | "not_started";
  start_time: string;
  end_time: string;
  effective_end_time: string | null;
  /** Null for a shift that has not started — there is nothing to report yet. */
  period_summary: AlertAnalyticsPeriodReport | null;
}

export interface AlertAnalyticsResponse {
  as_of: string;
  scope: {
    location_id: number;
    machine_ids: number[];
    device_mxids: string[];
    type_in: string[];
  };
  period: {
    mode: "local_day" | "absolute_range";
    local_date?: string | null;
    start_time: string;
    end_time: string;
    effective_end_time: string;
    provisional: boolean;
  };
  /**
   * ALWAYS the present state, even when a past period is selected — it answers
   * "what is waiting right now", which does not change because someone is
   * looking at last Tuesday. The UI must not imply it belongs to the period.
   */
  current_workload: {
    pending_count: number;
    oldest_pending: AlertAnalyticsOldestPending | null;
    carryover: {
      count: number | null;
      estimated_count: number | null;
      block_key?: string | null;
      block_started_at?: string | null;
      unavailable_reason?: string | null;
    };
  };
  /** Null when the backend has nothing to report for the period. */
  period_summary: AlertAnalyticsPeriodReport | null;
  /** Local-day mode only — an absolute range does not map onto one shift schedule. */
  shift_breakdown: AlertAnalyticsShift[] | null;
}

/** Scope, shared by both time modes. */
export interface AlertAnalyticsScope {
  location_id: number;
  machine_ids?: number[];
  device_mxids?: string[];
  type_in?: string[];
}

/**
 * The two time modes, as a union so "both" and "neither" are unrepresentable.
 * Only local-day responses carry a shift breakdown, which is why Day is the
 * default mode.
 */
export type AlertAnalyticsPeriod =
  | { mode: "day"; local_date: string }
  | { mode: "range"; start_time: string; end_time: string };

export interface AlertAnalyticsFilterState {
  scope: AlertAnalyticsScope;
  period: AlertAnalyticsPeriod;
}

/** How far back analytics can look. */
export const ANALYTICS_MAX_LOOKBACK_DAYS = 40;
